import { SubAccount } from '@icp-sdk/canisters/ledger/icp'
import { Principal } from '@icp-sdk/core/principal'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../appConfig'
import CyclesMinterClient from '../clients/CyclesMinterClient'
import { CYCLES_MINT_COMISSION, CYCLE_MINT_COMISSIONS_MEMO } from '../constants/business'
import {
  CYCLES_MINTER_CANISTER_ID,
  ICP_LEDGER,
  TCYCLES_CANISTER_ID,
} from '../constants/canisterIds'
import { transferToken } from '../handlers/walletActions/wallet'
import { ICToken } from '../interfaces/common'
import { GAEventName } from '../interfaces/googleAnalytics'
import { logger } from '../logger'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { useUserTokens } from '../state'
import { transferPlugCommission } from '../utils/transferPlugCommission'
import { decimalsToE8s, e8sToDecimals } from '../utils/validationHelper'
import useBalances from './useBalances'
import useConfig from './useConfig'
import useWallet from './useWallet'

const MINT_MEMO = 0x544e494d

const useMintCycles = () => {
  const [inputAmount, setInputAmount] = useState('')
  const [conversionRate, setConversionRate] = useState<number>()
  const [mintInProgress, setMintInProgress] = useState(false)
  const { tokens } = useConfig()

  const { findBalance, refreshBalance } = useBalances()
  const { wallet } = useWallet()
  const { addToken } = useUserTokens()
  const { fireEvent } = useGoogleAnalytics()

  const { t } = useTranslation()

  const icpToken = tokens.find((t) => t.address === ICP_LEDGER) as ICToken | undefined
  const tcycles = tokens.find((t) => t.address === TCYCLES_CANISTER_ID) as ICToken | undefined
  const icpBalance = findBalance({ address: ICP_LEDGER })

  const amounte8s = BigInt(decimalsToE8s(inputAmount, icpToken?.decimals || 8))

  useEffect(() => {
    CyclesMinterClient.getTCPerIcp()
      .then((rate) => {
        setConversionRate(rate * (1 - CYCLES_MINT_COMISSION))
      })
      .catch(logger.error)
  }, [])

  const outAmount = (Number(inputAmount) || 0) * (conversionRate || 0)

  const error = useMemo(() => {
    if (!Number(inputAmount)) return t('send.errors.noAmount')

    if (amounte8s + (icpToken?.fee || 0n) > (icpBalance?.balance || 0n)) {
      return t('send.errors.lowBalance', { token: 'ICP' })
    }
  }, [inputAmount, icpBalance])

  const performCyclesMint = async () => {
    if (!icpToken) return Promise.reject('No ICP token config')

    setMintInProgress(true)

    try {
      const fromAmount = Number(decimalsToE8s(inputAmount, icpToken.decimals))

      const plugCommission = Math.floor(fromAmount * CYCLES_MINT_COMISSION)

      transferPlugCommission(BigInt(plugCommission), icpToken, CYCLE_MINT_COMISSIONS_MEMO)

      const cyclesMinterClient = new CyclesMinterClient(
        config.getAgent({ principalId: wallet.principalId })
      )

      const subaccount = SubAccount.fromPrincipal(Principal.from(wallet.principalId)).toUint8Array()

      const transferHeight = await transferToken({
        receiver: {
          address: CYCLES_MINTER_CANISTER_ID,
          subaccount: Array.from(subaccount),
        },
        memo: MINT_MEMO,
        tokenCanisterId: icpToken.address,
        standard: icpToken.standard,
        amount: (fromAmount - plugCommission).toString(),
      })

      const notifyResult = await cyclesMinterClient.notifyMint(transferHeight as number)

      if ('Err' in notifyResult) {
        return Promise.reject(JSON.stringify(notifyResult.Err))
      }

      refreshBalance(icpToken)
      refreshBalance(tcycles!)

      addToken(wallet.principalId, tcycles!.id)

      setMintInProgress(false)

      if ('Ok' in notifyResult) {
        fireEvent(GAEventName.CYCLE_MINT, {
          amount: Number(notifyResult.Ok.minted),
        })

        return e8sToDecimals(notifyResult.Ok.minted, tcycles?.decimals || 12)
      }
    } finally {
      setMintInProgress(false)
    }

    return 0
  }

  return {
    inputAmount,
    setInputAmount,
    icpToken,
    conversionRate,
    outAmount,
    error,
    performCyclesMint,
    mintInProgress,
  }
}

export default useMintCycles
